import type NumberUnit from '@exodus/currency';
import { isEqual } from 'lodash';
import { useMemo, useState, useEffect } from 'react';

import type { Asset, FeesResult } from '@/types';
import exodus from '@/ui/exodus';
import type { Nft } from '../types/index.js';

type UseFeesParams = {
  amount?: NumberUnit;
  nft?: Nft;
  asset?: Asset;
  fromAddress?: string;
  toAddress: string;
  walletAccount: string;
  isSendAll: boolean;
};

const useFees = ({
  amount,
  nft,
  asset,
  fromAddress,
  isSendAll,
  toAddress,
  walletAccount,
}: UseFeesParams): { value: FeesResult; loading: boolean } => {
  const [fees, setFees] = useState<any>({});
  const [fetchingFees, setFetchingFees] = useState(false);

  useEffect(() => {
    if (!asset || !fromAddress) {
      return;
    }

    setFetchingFees(true);

    // note, this won't work when asset.address.validate is a remote call
    const isValidToAddress = !!toAddress && asset.address.validate(toAddress);

    exodus
      .getFees({
        amount,
        nft,
        assetName: asset.name,
        fromAddress,
        toAddress: isValidToAddress ? toAddress : undefined,
        isSendAll,
        walletAccount,
      })
      .then((newFees: FeesResult) => {
        setFees((previousFees) => {
          const unifiedFees = { ...previousFees, ...newFees };
          if (isEqual(previousFees, unifiedFees)) {
            return previousFees;
          }

          return unifiedFees;
        });
      })
      .finally(() => {
        setFetchingFees(false);
      });
  }, [asset, fromAddress, toAddress, amount, isSendAll, walletAccount, nft]);

  const value = useMemo(() => ({ ...fees, isSendAll }), [fees, isSendAll]);

  return { value, loading: fetchingFees };
};

export default useFees;
